import { Text } from '@/components/ui';
import { useAuthStore } from '@/lib/auth';
import { useSyncStore } from '@/lib/sync';
import { intlFormat } from 'date-fns';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TotalizerCard } from './components/TotalizerCard';
import { ExpiringContractCard } from './components/ExpiringContractCard';
import { Select, SelectOption } from '@/components/ui/Select';
import { DatePicker } from '@/components/ui/DatePicker';
import { DateRange } from 'react-day-picker';
import { Progress } from '@/components/ui/Progress';
import { TicketsChart } from './components/TicketsChart';
import { HoursChart } from './components/HoursChart';
import { useContractOptions } from '@/hooks/useContractOptions';
import { useCompanies } from '@/hooks/useCompanies';
import { useDashboard, useDashboardContractData } from '@/hooks/useDashboard';
import { Spinner } from '@/components/Spinner';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function DashboardPage() {
  const [selectedContractId, setSelectedContractId] = useState<string>('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  const { user } = useAuthStore();
  const { lastTicketsSync, lastContractsSync } = useSyncStore();
  const { t } = useTranslation();
  const { contractOptions } = useContractOptions({
    includeInactive: true,
    companyId: selectedCompanyId,
  });
  const { companies } = useCompanies({ limit: 100 });
  const { statistics, expiringContracts } = useDashboard();

  const startDate = dateRange?.from?.toISOString();
  const endDate = dateRange?.to?.toISOString();
  const {
    consumption,
    ticketsChart,
    hoursChart,
    isLoading: isLoadingContractData,
  } = useDashboardContractData(
    selectedContractId === 'all' ? undefined : selectedContractId,
    startDate,
    endDate
  );

  const greeting = useMemo(() => {
    const currentHour = new Date().getHours();

    if (currentHour >= 5 && currentHour < 12) {
      return t('common.greetings.morning');
    } else if (currentHour >= 12 && currentHour < 18) {
      return t('common.greetings.afternoon');
    } else {
      return t('common.greetings.evening');
    }
  }, []);

  const companyOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', content: t('common.all') },
      ...companies.map((company) => ({
        value: company.id,
        content: company.name,
      })),
    ],
    [companies, t]
  );

  // Reset contract selection when company changes
  useEffect(() => {
    setSelectedContractId('');
  }, [selectedCompanyId]);

  return (
    <div className="gap-2xl flex flex-1 flex-col">
      <header className="flex w-full items-center justify-between">
        <div>
          <Text size="md">
            {intlFormat(new Date(), { weekday: 'long', day: 'numeric', month: 'long' })
              .replace(/^./, (str) => str.toUpperCase())
              .replace(
                /\b([a-z])([a-z]+)\s*$/,
                (_match, first, rest) => first.toUpperCase() + rest
              )}
          </Text>
          <Text as="h1" variant="heading">
            {greeting}, <strong className="text-neutral-strong">{user?.name}</strong>
          </Text>
        </div>
        <div className="gap-md flex flex-col items-end">
          {(lastTicketsSync || lastContractsSync) && (
            <div className="gap-xxs flex flex-col text-right">
              {lastContractsSync && (
                <Text variant="label" size="sm" className="text-neutral-muted">
                  {t('dashboard.lastContractsSync')}{' '}
                  {formatDistanceToNow(new Date(lastContractsSync), {
                    addSuffix: true,
                    locale: ptBR,
                  })}
                </Text>
              )}
              {lastTicketsSync && (
                <Text variant="label" size="sm" className="text-neutral-muted">
                  {t('dashboard.lastTicketsSync')}{' '}
                  {formatDistanceToNow(new Date(lastTicketsSync), {
                    addSuffix: true,
                    locale: ptBR,
                  })}
                </Text>
              )}
            </div>
          )}
        </div>
      </header>

      <section className="gap-xs flex flex-col">
        <Text size="md" strong>
          {t('dashboard.overviewTitle')}
        </Text>
        <div className="gap-xs grid grid-cols-4">
          <TotalizerCard
            title={t('dashboard.activeContracts')}
            currentValue={statistics?.activeContracts.currentValue || 0}
            monthlyChange={statistics?.activeContracts.monthlyChange || 0}
          />
          <TotalizerCard
            title={t('dashboard.expiringContracts')}
            currentValue={statistics?.expiringContracts.currentValue || 0}
            monthlyChange={statistics?.expiringContracts.monthlyChange || 0}
          />
          <TotalizerCard
            title={t('dashboard.processedTicketsThisMonth')}
            currentValue={statistics?.processedTickets.currentValue || 0}
            monthlyChange={statistics?.processedTickets.monthlyChange || 0}
          />
          <TotalizerCard
            title={t('dashboard.processedHoursThisMonth')}
            currentValue={statistics?.processedHours.currentValue || 0}
            monthlyChange={statistics?.processedHours.monthlyChange || 0}
            suffix="h"
          />
        </div>
      </section>

      <section className="gap-xs flex flex-col">
        <Text size="md" strong>
          {t('dashboard.expiringContractsSection')}
        </Text>
        <div className="gap-xs grid grid-cols-4">
          {expiringContracts.map((contract) => (
            <ExpiringContractCard
              key={contract.id}
              contractName={contract.contractName}
              status={contract.status}
              expirationDate={new Date(contract.expirationDate)}
              hoursConsumed={contract.hoursConsumed}
              totalHours={contract.totalHours}
            />
          ))}
          {expiringContracts.length === 0 && (
            <Text variant="label" className="text-neutral-muted col-span-4 py-4 text-center">
              {t('dashboard.noExpiringContracts')}
            </Text>
          )}
        </div>
      </section>

      <section className="gap-xs flex">
        <Select
          options={companyOptions}
          placeholder={t('dashboard.filterByCompany')}
          width={276}
          value={selectedCompanyId === 'all' ? '' : selectedCompanyId}
          onValueChange={(value) => setSelectedCompanyId(value || 'all')}
        />
        <Select
          options={contractOptions}
          placeholder={t('dashboard.filterByContract')}
          width={276}
          value={selectedContractId}
          onValueChange={(value) => setSelectedContractId(value || '')}
        />
        <DatePicker
          placeholder={t('dashboard.filterByDate')}
          width={276}
          mode="range"
          value={dateRange}
          onChange={(date) => setDateRange(date as DateRange | undefined)}
        />
      </section>

      {selectedContractId && (
        <section className="gap-xs flex flex-col">
          <Text size="md" strong>
            {t('dashboard.hoursBucket')}
          </Text>
          <div>
            <Text variant="label" size="xs" className="text-neutral-muted">
              {t('dashboard.hoursConsumed')}{' '}
              <strong className="text-neutral-default font-medium">
                {consumption?.hoursConsumed || 0}h/{consumption?.totalHours || 0}h
              </strong>
            </Text>
            <Text variant="label" size="xs" className="text-neutral-muted">
              {t('dashboard.contractEnd')}{' '}
              <strong className="text-neutral-default font-medium">
                {consumption?.contractEndDate
                  ? new Date(consumption.contractEndDate).toLocaleDateString('pt-BR')
                  : '—'}
              </strong>
            </Text>
          </div>
          <Progress value={consumption?.percentageUsed || 0} />
        </section>
      )}

      {selectedContractId && dateRange && (
        <section className="gap-xs flex flex-col">
          <Text size="md" strong>
            {t('dashboard.companyConsumption')}
          </Text>
          <div className="gap-xs grid grid-cols-2">
            <TicketsChart data={ticketsChart?.data} totalTickets={ticketsChart?.total} />
            <HoursChart data={hoursChart?.data} totalHours={hoursChart?.total} />
          </div>
        </section>
      )}

      {!selectedContractId && !dateRange && !isLoadingContractData && (
        <section className="py-2xl text-center">
          <Text variant="label" className="text-neutral-muted">
            {t('dashboard.selectContractAndPeriod')}
          </Text>
        </section>
      )}

      {isLoadingContractData && (
        <section className="py-2xl flex items-center justify-center">
          <Spinner size={32} color="black" />
        </section>
      )}
    </div>
  );
}
